/**
 * Derives the richer renderer color slots from a theme's 14 canonical keys so
 * paged-flip (PagePalette) and detailed legacy templates get precise values
 * instead of relying on runtime alias fallbacks.
 *
 * Slot names follow CanonicalColors in
 * app/dashboard/templates/components/lib/templateVariables.ts.
 */

import type { MenuThemeColors, MenuThemeMode, MenuThemePackage } from './registry';
import { resolveMenuThemeColors } from './registry';
import { blackOrWhiteForeground, readableForeground } from './contrast';

export interface MenuThemeColorValues extends Omit<MenuThemeColors, 'gradients'> {
  foreground: string;
  primaryForeground: string;
  secondaryForeground: string;
  accentForeground: string;
  panel: string;
  panelBorder: string;
  heading: string;
  subheading: string;
  body: string;
  sectionTitle: string;
  itemTitle: string;
  itemDesc: string;
  price: string;
  mutedForeground: string;
  /**
   * Gradient layers flattened to plain string slots ('' when the theme has
   * none) so applying a non-gradient theme clears a previous theme's
   * gradients. Renderers treat '' as "no gradient".
   */
  backgroundGradient: string;
  promoGradient: string;
}

/**
 * Derivation follows the push direction of COLOR_ALIASES in
 * lib/mnrq/template-variable-standards.ts (primary → titles, muted →
 * descriptions, accent → price) so a later manual edit of a canonical color
 * updates the same slots a theme filled.
 */
export function deriveMenuThemeColorValues(colors: MenuThemeColors): MenuThemeColorValues {
  const { gradients, ...solidColors } = colors;
  return {
    ...solidColors,
    backgroundGradient: gradients?.background || '',
    promoGradient: gradients?.promo || '',
    foreground: readableForeground(colors.background, colors.text),
    primaryForeground: blackOrWhiteForeground(colors.primary),
    secondaryForeground: blackOrWhiteForeground(colors.secondary),
    accentForeground: blackOrWhiteForeground(colors.accent),
    panel: colors.surface,
    panelBorder: colors.divider,
    heading: colors.primary,
    subheading: colors.muted,
    body: colors.text,
    sectionTitle: colors.primary,
    itemTitle: colors.primary,
    itemDesc: colors.muted,
    price: colors.accent,
    mutedForeground: colors.muted,
  };
}

/** Full color value map (canonical + derived) for a theme in a given mode. */
export function buildThemeColorValues(
  theme: MenuThemePackage,
  mode?: MenuThemeMode,
): { mode: MenuThemeMode; values: MenuThemeColorValues } {
  const resolved = resolveMenuThemeColors(theme, mode);
  return { mode: resolved.mode, values: deriveMenuThemeColorValues(resolved.colors) };
}
